/**
 * Consult Door B — tiny inline icon set (currentColor, aria-hidden).
 * Kept local to the feature so the public page pulls in no icon dependency.
 */
import type { SVGProps } from 'react';

type P = SVGProps<SVGSVGElement> & { size?: number };

function base(size: number, props: P) {
  const { size: _omit, ...rest } = props;
  void _omit;
  return {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    'aria-hidden': true as const,
    focusable: false as const,
    ...rest,
  };
}

export const IconCheck = (p: P) => (
  <svg {...base(p.size ?? 16, p)}>
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

export const IconArrowRight = (p: P) => (
  <svg {...base(p.size ?? 16, p)} strokeWidth={2.4}>
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

export const IconArrowLeft = (p: P) => (
  <svg {...base(p.size ?? 15, p)} strokeWidth={2.4}>
    <path d="M19 12H5M11 18l-6-6 6-6" />
  </svg>
);

export const IconChevronRight = (p: P) => (
  <svg {...base(p.size ?? 18, p)} strokeWidth={2.2}>
    <path d="M9 6l6 6-6 6" />
  </svg>
);

export const IconClock = (p: P) => (
  <svg {...base(p.size ?? 14, p)}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </svg>
);

export const IconGlobe = (p: P) => (
  <svg {...base(p.size ?? 15, p)}>
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18M12 3a14 14 0 0 1 0 18 14 14 0 0 1 0-18z" />
  </svg>
);

export const IconMeet = (p: P) => (
  <svg {...base(p.size ?? 16, p)}>
    <rect x="2" y="6" width="13" height="12" rx="2" />
    <path d="M15 10l6-3v10l-6-3z" />
  </svg>
);

export const IconCalendar = (p: P) => (
  <svg {...base(p.size ?? 16, p)}>
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <path d="M16 2v4M8 2v4M3 10h18M12 14v4M10 16h4" />
  </svg>
);

export const IconMail = (p: P) => (
  <svg {...base(p.size ?? 18, p)}>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="M3 7l9 6 9-6" />
  </svg>
);

export const IconChat = (p: P) => (
  <svg {...base(p.size ?? 18, p)}>
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

export const IconExternal = (p: P) => (
  <svg {...base(p.size ?? 16, p)}>
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <path d="M15 3h6v6M10 14L21 3" />
  </svg>
);

export const IconPencil = (p: P) => (
  <svg {...base(p.size ?? 12, p)} strokeWidth={2.4}>
    <path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
  </svg>
);

export const IconInfo = (p: P) => (
  <svg {...base(p.size ?? 18, p)}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8v4M12 16h.01" />
  </svg>
);

export const IconCancel = (p: P) => (
  <svg {...base(p.size ?? 34, p)}>
    <circle cx="12" cy="12" r="9" />
    <path d="M15 9l-6 6M9 9l6 6" />
  </svg>
);

export const IconCopy = (p: P) => (
  <svg {...base(p.size ?? 16, p)}>
    <rect x="9" y="9" width="11" height="11" rx="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

export const IconBrowser = (p: P) => (
  <svg {...base(p.size ?? 20, p)}>
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18M12 3c2.5 2.5 3.8 5.7 3.8 9s-1.3 6.5-3.8 9c-2.5-2.5-3.8-5.7-3.8-9S9.5 5.5 12 3z" />
  </svg>
);
